<script>
import appConfig from '@src/app.config'
import Layout from '@layouts/main.vue'
import AssignDomainModal from '@components/modals/AssignDomainModal/AssignDomainModal.vue'
import WidgetStepPreview from '@components/Widgets/_global/WidgetStepPreview/WidgetStepPreview.vue'
import { mapActions, mapGetters, mapState } from 'vuex'
import isTabletMixin from '@mixins/isTablet'
import uniquifyObject from '@utils/uniquifyObject'
import { CAMPAIGN_STATUS, CAMPAIGN_TYPE } from '@meta/campaignConstants'
import { errorMessages } from '@meta/errorMessages'

export default {
  page: {
    title: 'Dashboard',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    'assign-domain-modal-component': AssignDomainModal,
    WidgetStepPreview,
  },
  mixins: [isTabletMixin],
  inject: ['$parseDate'],
  data() {
    return {
      campaignStatus: CAMPAIGN_STATUS,
      campaignType: CAMPAIGN_TYPE,
      filter: 'all',
      campaignsStatusMapping: {},
      assignDomainModalVisible: false,
      targetCampaignUUID: '',
      onActionComplete: null,
    }
  },
  computed: {
    ...mapState({
      userToken: (state) => state.users.currentUser.apiKey,
    }),
    ...mapState('campaign', [
      'currentCampaignsList',
      'areCampaignsLoaded',
      'campaignPartialSaveCompleted',
      'campaignPartialSaveError',
      'campaignActionErrorMessage',
      'campaignCreationCompleted',
      'campaignCreationError',
      'campaignCreationLoading',
      'campaignActionErrorMessage',
    ]),
    ...mapState('campaign/analytics/stats', [
      'getStatsCompleted',
    ]),
    ...mapGetters('campaign/analytics/stats', [
      'allCampaignImpressionsByUUID',
      'allCampaignConversionsByUUID',
      'allCampaignLeadsByUUID',
    ]),
    ...mapState('plans/featureLimits', [
      'pageViews',
      'getPlanLimitsCompleted',
    ]),
    isFeatureLimitedPageViews() {
      return this.$store.getters['plans/featureLimits/isFeatureLimited.pageViews']
    },
    dynamicCampaignData() {
      // No filtering needed if filter is set to 'all'
      if (this.filter === 'all') {
        return this.currentCampaignsList.filter(({ status }) =>
            status.toLowerCase() !== CAMPAIGN_STATUS.ARCHIVED,
        )
      }

      return this.currentCampaignsList.filter(({ status }) =>
          status.toLowerCase() === this.filter.toLowerCase(),
      )
    },
  },
  watch: {
    areCampaignsLoaded: {
      handler(loaded) {
        if (loaded) {
          this.setCampaignStatusMapping()
        } else {
          this.$nextTick(this.setCampaignStatusMapping)
        }
      },
      immediate: true,
    },
  },
  methods: {
    ...mapActions('campaign', ['createCampaign', 'deleteCampaign', 'partialSaveCampaign']),
    async confirmAction(action, message, title, confirmButtonText, cancelButtonText, type = 'warning') {
      try {
        await this.$msgbox.confirm(message, title, {
          confirmButtonText,
          cancelButtonText,
          showCancelButton: true,
          type,
        })
        action()
      } catch (error) {
        // console.log('Action cancelled')
      }
    },
    triggerDeleteCampaign(campaignUUID) {
      this.confirmAction(
          () => this.deleteCampaign(campaignUUID),
          'Are you sure you\'d like to delete this campaign?',
          'Warning',
          'Delete',
          'Cancel',
      )
    },
    getUniqueWidgetTemplate(campaignUUID) {
      const campaign = this.currentCampaignsList.find((campaign) => {
        return campaign.uuid === campaignUUID
      })
      if (!campaign) return null

      const campaignClone = JSON.parse(JSON.stringify(campaign))

      delete campaignClone.id
      delete campaignClone.uuid
      delete campaignClone.status
      delete campaignClone.verified

      campaignClone.widgetItems.forEach((widget) => {
        delete widget.id
      })

      return uniquifyObject(campaignClone)
    },
    triggerDuplicateCampaign(campaignUUID) {
      const originalCampaign = this.currentCampaignsList.find(campaign => campaign.uuid === campaignUUID)
      if (!originalCampaign) return

      const baseName = 'Copy of ' + originalCampaign.name
      let newName = baseName
      let copyNumber = 1

      // Check if the campaign name already exists and append a number to make it unique
      while (this.currentCampaignsList.some(campaign => campaign.name === newName)) {
        newName = `${baseName} #${copyNumber++}`
      }

      const campaignToDuplicate = { ...this.getUniqueWidgetTemplate(campaignUUID), name: newName }

      if (!campaignToDuplicate.domainID) {
        this.onActionComplete = (domainID) => {
          this.duplicateCampaign({ ...campaignToDuplicate, domainID })
        }
        this.assignDomainModalVisible = true
      } else {
        this.duplicateCampaign(campaignToDuplicate)
      }
    },
    duplicateCampaign(campaign) {
      this.createCampaign({
        ...campaign,
      }).then((response) => {
        if (this.campaignCreationCompleted && !this.campaignCreationError) {
          this.$router.push({ path: `/editor/${response.data.uuid}` })
        } else if (this.campaignCreationError) {
          this.$message({
            type: 'error',
            message: this.campaignActionErrorMessage,
          })
        }
      })
    },
    triggerArchiveCampaign(campaignUUID) {
      this.triggerSetCampaignStatus(campaignUUID, '', CAMPAIGN_STATUS.ARCHIVED) // Assuming '' for domainID as it's not relevant to archiving.
    },
    setCampaignStatusMapping() {
      this.currentCampaignsList.forEach(({ uuid, status }) => {
        this.$set(this.campaignsStatusMapping, uuid, status === CAMPAIGN_STATUS.ACTIVE)
      })
    },
    toggleCampaignStatus(campaignUUID, domainID) {
      const newStatus = this.campaignsStatusMapping[campaignUUID] ? CAMPAIGN_STATUS.INACTIVE : CAMPAIGN_STATUS.ACTIVE
      this.triggerSetCampaignStatus(campaignUUID, domainID, newStatus)
    },
    async triggerSetCampaignStatus(campaignUUID, domainID, newStatus) {
      this.targetCampaignUUID = campaignUUID

      if (!campaignUUID || !newStatus) {
        this.$message({
          type: 'error',
          message: errorMessages.default,
        })
        return
      }

      if (newStatus === CAMPAIGN_STATUS.ACTIVE && domainID === '') {
        this.onActionComplete = (domainID) => {
          this.setCampaignStatus(campaignUUID, domainID, newStatus)
        }
        this.assignDomainModalVisible = true
      } else {
        this.setCampaignStatus(campaignUUID, domainID, newStatus)
      }
    },
    async setCampaignStatus(campaignUUID, domainID, newStatus) {
      try {
        await this.partialSaveCampaign({ uuid: campaignUUID, status: newStatus, domainID }).then((response) => {
          if (this.campaignPartialSaveCompleted && !this.campaignPartialSaveError) {
            // Update the local state to reflect the change
            this.campaignsStatusMapping[campaignUUID] = newStatus === CAMPAIGN_STATUS.ACTIVE
            this.$message({
              type: 'success',
              message: `Your campaign is now ${newStatus}.`,
            })
          } else if (this.campaignPartialSaveError) {
            this.$message({
              type: 'error',
              message: this.campaignActionErrorMessage,
            })
          }
        })
      } catch (error) {
        console.log('Operation cancelled or failed')
      }
    },
    triggerFilter(key) {
      this.filter = this.$refs['el-menu'].$children[key - 1].$el.dataset.value
    },
    openEditor(index, row) {
      const campaignUUID = row.uuid
      this.$router.push({ path: `/editor/${campaignUUID}` })
    },
    openSettings(index, row) {
      const campaignUUID = row.uuid
      this.$router.push({ path: `/setup/${campaignUUID}` })
    },
  },
}
</script>

<template>
  <Layout>
    <el-row id="dashboard-view">
      <el-col :span="24" :md="{ span: 22, offset: 1 }">
        <el-row>
          <el-col :xs="24" :sm="12">
            <h1 class="has-subheading">Campaigns</h1>
            <p>Easily navigate and manage your campaigns</p>
          </el-col>
          <el-col :xs="24" :sm="12">
            <div :class="$style.pageViewsContainer">
              <template v-if="!getPlanLimitsCompleted">
                <Loader class="!mr-0"></Loader>
              </template>
              <template v-else>
                <el-popover
                    placement="bottom"
                    title="Limit Exceeded"
                    width="200"
                    trigger="hover"
                    content="You have exceeded your plan limit for this month"
                    :disabled="!isFeatureLimitedPageViews">
                  <strong slot="reference" class="text-3xl" :class="{ 'color-danger': isFeatureLimitedPageViews }">
                    <i v-if="isFeatureLimitedPageViews" class="el-icon-warning-outline"></i>
                    {{ pageViews.current }} / <b class="text-lg">{{ pageViews.limit | numberWithCommas }}</b>
                  </strong>
                </el-popover>
                <span class="text-sm">Overall Impressions this month</span>
              </template>
            </div>
          </el-col>
        </el-row>

        <el-container class="container-lg" :direction="isTablet ? 'vertical' : 'horizontal'">
          <el-aside v-if="currentCampaignsList.length > 0" width="250px" class="c-aside">
            <el-menu
                ref="el-menu"
                default-active="1"
                :text-color="'#333'"
                :active-text-color="'#000'"
                :background-color="'transparent'"
                @select="triggerFilter">
              <el-menu-item index="1" data-value="all">
                <i class="el-icon-tickets"></i>
                <span>All</span>
              </el-menu-item>
              <el-menu-item index="2" :data-value="campaignStatus.INACTIVE">
                <i class="el-icon-edit-outline"></i>
                <span>Inactive</span>
              </el-menu-item>
              <el-menu-item index="3" :data-value="campaignStatus.ACTIVE">
                <i class="el-icon-view"></i>
                <span>Active</span>
              </el-menu-item>
              <div class="flex items-center">
                <div class="flex-1 border-t-2 border-gray-200"></div>
                <span class="px-3 text-gray-500 text-xs">Other</span>
                <div class="flex-1 border-t-2 border-gray-200"></div>
              </div>
              <!--              <hr class="border-t-2">-->
              <el-menu-item index="4" :data-value="campaignStatus.ARCHIVED">
                <i class="el-icon-document"></i>
                <span>Archived</span>
              </el-menu-item>
            </el-menu>
          </el-aside>

          <template v-if="areCampaignsLoaded">
            <el-main :class="$style.elMain">
              <el-table
                  style="width: 100%"
                  :data="dynamicCampaignData"
                  :default-expand-all="true">
                <el-table-column :width="'260px'" label="">
                  <template slot-scope="scope">
                    <div :class="$style.campaignHeader" class="flex">
                      <div class="flex flex-col gap-y-8">
                        <div>
                          <h4 class="font-sans font-semibold">{{ scope.row.name }}</h4>
                          <el-popover
                              v-if="isFeatureLimitedPageViews"
                              class="!mr-0"
                              placement="top"
                              trigger="hover">
                            <strong slot="reference">
                              <i title="Campaign Locked" class="el-icon-lock text-xl"></i>
                            </strong>
                            <p>This campaign is not collecting any leads currently.</p>
                            <router-link :to="{ name: 'billing' }"
                                         class="underline text-sm text-onvocado-red float-right">Upgrade now
                            </router-link>
                          </el-popover>

                          <br/>
                          <div class="mb-2">
                            <a :href="`http://preview.onvocado.com/?key=${userToken}&campaign_id=${scope.row.uuid}`"
                               target="_blank"
                               rel="noreferrer">
                              Preview Link <i class="el-icon-top-right"></i>
                            </a>
                          </div>
                          <div v-if="scope.row.domain">
                            <a
                                :href="scope.row.domain"
                                target="_blank"
                                rel="noreferrer">
                              {{ scope.row.domain }}
                              <i v-if="scope.row.verified" class="el-icon-top-right"></i>
                            </a>

                            <el-popover
                                v-if="!scope.row.verified"
                                class="ml-2 !mr-0"
                                placement="top"
                                trigger="hover">
                              <strong slot="reference">
                                <i class="el-icon-warning text-base text-onvocado-red"></i>
                              </strong>
                              <p>Onvocado pixel not found on this domain.</p>
                              <router-link v-if="!scope.row.verified"
                                           :to="{ name: 'pixel' }"
                                           class="underline text-sm text-onvocado-red float-right">Embed now
                              </router-link>
                            </el-popover>
                          </div>
                        </div>

                        <div class="ml-2 flex flex-col gap-y-2">
                          <div v-for="(widgetItem, index) in scope.row.widgetItems" :key="index" class="flex">
                            <!--                            <div v-if="scope.row.type === campaignType.AB" class="flex items-center justify-center">-->
                            <!--                              <span class="text-primary text-sm">-&#45;&#45; {{ widgetItem.trafficShare * 100 }}% -&#45;&#45;</span>-->
                            <!--                            </div>-->

                            <div class="w-44 relative rounded-xl cursor-pointer overflow-hidden"
                                 :class="$style.widgetStepPreviewContainer"
                                 @click="openEditor(scope.$index, scope.row)">
                              <WidgetStepPreview :widget="widgetItem"/>
                            </div>
                          </div>
                        </div>
                        <div v-if="scope.row.scheduling_enabled"
                             class="ml-4 text-xs inline-flex items-center font-bold leading-sm uppercase px-3 py-1
                              bg-onvocado-secondary-light text-onvocado-gray-darker rounded-full">
                          Scheduling Enabled
                        </div>
                      </div>
                    </div>
                  </template>
                </el-table-column>
                <el-table-column class-name="!align-top">
                  <template slot-scope="props">
                    <div class="h-full flex flex-col gap-y-8">

                      <div :class="$style.campaignFooter">
                        <div v-if="props.row.status !== campaignStatus.ARCHIVED">
                          <!-- eslint-disable vue/valid-v-model -->
                          <el-switch
                              :value="campaignsStatusMapping[props.row.uuid]"
                              active-text="active"
                              @change="toggleCampaignStatus(props.row.uuid, props.row.domainID)"></el-switch>
                          <!--eslint-enable-->
                        </div>
                        <el-dropdown
                            type="info"
                            size="medium"
                            trigger="click"
                            split-button
                            @click="openEditor(props.$index, props.row)">
                          Edit
                          <el-dropdown-menu slot="dropdown">
                            <el-dropdown-item
                                @click.native="openSettings(props.$index, props.row)">
                              Settings
                            </el-dropdown-item>

                            <el-dropdown-item
                                divided
                                @click.native="triggerDuplicateCampaign(props.row.uuid)">
                              Duplicate
                            </el-dropdown-item>

                            <el-dropdown-item
                                v-if="props.row.status !== campaignStatus.ARCHIVED"
                                @click.native="triggerArchiveCampaign(props.row.uuid)">
                              Archive
                            </el-dropdown-item>

                            <el-dropdown-item
                                @click.native="triggerDeleteCampaign(props.row.uuid)">
                              Delete
                            </el-dropdown-item>
                          </el-dropdown-menu>
                        </el-dropdown>
                      </div>

                      <template v-if="getStatsCompleted">
                        <div :class="$style.campaignInsights">
                          <div :class="$style.insightItem">
                            <span>{{ allCampaignImpressionsByUUID(props.row.uuid) }}</span>
                            <p>impressions</p>
                            <!--<p>views</p>-->
                          </div>
                          <div :class="$style.insightItem">
                            <span>{{ allCampaignConversionsByUUID(props.row.uuid) }}</span>
                            <p>conversions</p>
                          </div>
                          <div :class="$style.insightItem">
                            <span>{{ allCampaignLeadsByUUID(props.row.uuid) }}</span>
                            <p>leads</p>
                            <!-- <p>Conversion rate (i.e. 0.00%)</p> -->
                          </div>
                        </div>
                      </template>
                      <template v-else>
                        <Loader></Loader>
                      </template>
                      <!--                    </div>-->
                    </div>
                  </template>
                </el-table-column>

                <template slot="empty">
                  <router-link v-if="filter === 'all'" :to="{ name: 'templates' }">
                    <div :class="$style.empty"
                         class="max-w-[200px] mx-auto my-4 flex flex-col justify-center items-center gap-2 bg-onvocado-primary hover:bg-onvocado-primary-light rounded-xl shadow-md">
                      <i class="el-icon-circle-plus-outline text-5xl"></i>
                      <h3 class="text-xl">Create</h3>
                    </div>
                  </router-link>

                  <p v-else>You currently have no {{ filter }} campaigns.</p>
                </template>
              </el-table>
            </el-main>
          </template>

          <template v-else>
            <Loader></Loader>
          </template>
        </el-container>
      </el-col>
    </el-row>

    <assign-domain-modal-component :modal-visible="assignDomainModalVisible"
                                   :target-campaign="targetCampaignUUID"
                                   :on-action-complete="onActionComplete"
                                   @close-modal="assignDomainModalVisible = false"/>
  </Layout>
</template>

<style lang="scss">
@import '@assets/sass/design/_mixin.scss';

#dashboard-view {
  .c-aside {
    @include asideMenu;
  }

  .el-table {
    background-color: transparent;

    &__body {
      height: 1px;
      border-spacing: 0 1rem;

      .cell {
        height: 100%;
      }
    }

    tr,
    td,
    th {
      background-color: transparent !important;
      border: none;
    }

    tr > td {
      @apply border-0 border-b-2 border-solid border-gray-200;
      padding: 10px 10px 20px 10px;
      //border-bottom: 2px solid #ececec;
    }

    &__expand-column {
      pointer-events: none;
      opacity: 0;
    }
  }
}
</style>

<style lang="scss" module>
@import '@assets/sass/design/_colors.scss';

.pageViewsContainer {
  @apply mb-4 mx-4 md:mt-16 md:mr-10 flex flex-col;
  text-align: right;

  i {
    margin-right: 10px;
  }

  strong {
    @apply font-ease-standard font-bold;
    &:focus {
      outline: none;
    }
  }
}

.elMain {
  padding: 0 20px 0 20px;
}

.campaignHeader {
  @apply w-full;
  /* Fixes nasty whitespace bug */
  font-size: 0;

  i {
    padding: 0;
  }

  h3 {
    @apply mr-2 text-xl inline-block;
  }

  span {
    @apply mr-2 font-bold;
  }

  button {
    @apply text-onvocado-secondary;

    padding: 0;
    vertical-align: super;
  }

  a {
    @apply text-sm text-tertiary;

    text-decoration: none;
  }
}

.campaignInsights {
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.insightItem {
  margin: 0 20px;
  text-align: center;

  span {
    @apply text-xl text-heading font-ease-standard font-bold;

    font-weight: 600;
  }

  p {
    @apply text-sm font-medium tracking-wide;
  }
}

.campaignStatus {
  @apply bg-onvocado-gray-light;

  padding: 2px 9px;
  border-radius: 50px;
}

.widgetStepPreviewContainer {
  // box shadow size 3px
  margin: 3px 15px 3px 3px;
  box-shadow: 0 0 0 3px $color-contrast-light;
  aspect-ratio: 16 / 10;
}

.campaignFooter {
  @apply text-xs;

  display: flex;
  align-items: baseline;
  justify-content: space-between;
}

.empty {
  aspect-ratio: 1/1;

  i {
    @apply rotate-90 transition-transform;
  }
}
</style>
